import { useCallback, useEffect, useMemo, useState } from "react";

const getStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const orderColumns = (allColumnIds, activeSet) => {
  if (!Array.isArray(allColumnIds) || !allColumnIds.length) {
    return Array.from(activeSet);
  }
  return allColumnIds.filter((id) => activeSet.has(id));
};

export const useColumnPreferences = ({
  allColumnIds = [],
  defaultVisibleIds = [],
  storageKey,
} = {}) => {
  const initialVisible = useMemo(() => {
    if (!storageKey) return [...defaultVisibleIds];
    const storage = getStorage();
    if (!storage) return [...defaultVisibleIds];
    try {
      const parsed = JSON.parse(storage.getItem(storageKey) || "[]");
      if (Array.isArray(parsed) && parsed.length) {
        return allColumnIds.length
          ? allColumnIds.filter((id) => parsed.includes(id))
          : parsed.filter((id) => typeof id === "string");
      }
    } catch {
      /* ignore malformed values */
    }
    return [...defaultVisibleIds];
  }, [allColumnIds, defaultVisibleIds, storageKey]);

  const [visibleColumns, setVisibleColumns] = useState(initialVisible);

  useEffect(() => {
    if (!storageKey) return;
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.setItem(storageKey, JSON.stringify(visibleColumns));
    } catch {
      /* ignore persistence errors */
    }
  }, [visibleColumns, storageKey]);

  const toggleColumn = useCallback(
    (columnId) => {
      if (!columnId) return;
      setVisibleColumns((prev) => {
        const active = new Set(prev);
        if (active.has(columnId)) {
          active.delete(columnId);
          if (!active.size) return prev;
        } else {
          active.add(columnId);
        }
        return orderColumns(allColumnIds, active);
      });
    },
    [allColumnIds]
  );

  const resetColumns = useCallback(() => {
    setVisibleColumns([...defaultVisibleIds]);
  }, [defaultVisibleIds]);

  const selectAllColumns = useCallback(() => {
    if (allColumnIds.length) {
      setVisibleColumns([...allColumnIds]);
    } else {
      setVisibleColumns([...defaultVisibleIds]);
    }
  }, [allColumnIds, defaultVisibleIds]);

  return {
    visibleColumns,
    setVisibleColumns,
    toggleColumn,
    resetColumns,
    selectAllColumns,
  };
};
